import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { notification, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Assets } from "../../../constants/images";
import { GetAuthUserLocalStorage } from "../../../services/localStorage/localStorage";
import {
  setAllUsers,
  setIsAllUsersLoading,
} from "../../../store/slices/chatsSlice";
import { GetChatUsers, GetMembers } from "../../../services/team/member";
import { AddIcon, ToastCrossIcon } from "../../../constants/svg";
import Image from "antd/es/image";
import { isUserAlreadyChatWithEachOther } from "../../../services/chats/frontend-chats";
import { roles as salonRoles } from "../../../utils/constants";
import AvatarImage from "../../Common/AvatarImage";
import { sortBy } from "lodash";
import { setCreateUpdateGroupForm } from "../../../store/slices/groupSlice";
import GenericModal from "../../Modals/GenericModal";
import CreateNewGroup from "../../Modals/CreateNewGroup";

const { Option } = Select;

const NewChat = forwardRef(({ setNewChat, setNewChatDefault }, ref) => {
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [enterPressed, setEnterPressed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { allUsers } = useSelector((state) => state?.chats);
  const { me } = useSelector((state) => state.auth);
  const { createUpdateGroupForm } = useSelector((state) => state.group);
  const authUser = GetAuthUserLocalStorage();
  const dispatch = useDispatch();

  // Debounce timeout ref
  const debounceTimeout = useRef(null);

  const resetForm = () => {};

  // Select component ref for focus management
  const selectRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openSendTo: () => {
      setDropdownOpen(true);
      setTimeout(() => selectRef.current?.focus(), 0);
    },
  }));
  const handleMemberSelect = async (user_id) => {
    if (!enterPressed) {
      setDropdownOpen(false);
      setNewChat(false);
      setNewChatDefault && setNewChatDefault(false);
      await isUserAlreadyChatWithEachOther(
        parseInt(authUser?.id),
        parseInt(user_id),
        dispatch,
        allUsers.data,
      );
    } else {
      setSearchTerm("");
    }
  };

  // Handle dropdown open/close events
  const handleDropdownOpenChange = (open) => {
    setDropdownOpen(open);
  };

  // Handle focus to open dropdown
  const handleFocus = () => {
    setDropdownOpen(true);
  };
  const fetchAllUsers = async () => {
    dispatch(setIsAllUsersLoading(true));
    let response;
    if (GetAuthUserLocalStorage()?.roles[0]?.id == salonRoles.super_admin) {
      response = await GetChatUsers({
        keyword: debouncedSearchTerm || "",
        light: 1,
      });
    } else {
      response = await GetMembers({ keyword: debouncedSearchTerm });
    }
    const { data, status, message } = response.data;
    if (!status) {
      dispatch(setIsAllUsersLoading(false));
      notification.error({
        description: message,
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
      return;
    }
    let users =
      GetAuthUserLocalStorage()?.roles[0]?.id == salonRoles.super_admin
        ? data
        : data.users;
    users =
      users.length > 0
        ? users?.filter((item) => {
            return item.id != authUser?.id;
          })
        : users;
    dispatch(setAllUsers(users));
    dispatch(setIsAllUsersLoading(false));
  };

  // Debounced search effect - triggers API calls
  useEffect(() => {
    if (debouncedSearchTerm === "" || debouncedSearchTerm.length >= 3) {
      fetchAllUsers();
    }
  }, [debouncedSearchTerm]);

  // Initial load effect - ensures first load happens
  useEffect(() => {
    // Set initial debounced term to trigger first load
    setDebouncedSearchTerm("");
  }, []);

  // Debounced search handler
  const handleDebouncedSearch = (value) => {
    setSearchTerm(value); // Immediate UI feedback

    // Open dropdown when user starts typing
    if (value && value.length > 0 && !dropdownOpen) {
      setDropdownOpen(true);
    }

    // Clear existing timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Set new timeout for debounced search
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 500); // 500ms debounce delay
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Restore focus after component remounts due to loading state change
  useEffect(() => {
    if (
      !allUsers?.isLoading &&
      dropdownOpen &&
      selectRef.current &&
      searchTerm
    ) {
      // Small delay to ensure component is fully mounted
      setTimeout(() => {
        selectRef.current?.focus();
      }, 50);
    }
  }, [allUsers?.isLoading, dropdownOpen, searchTerm]);

  const handleKeyPress = (e) => {
    if (e.key == "Enter") {
      e.preventDefault();
      setEnterPressed(true);
    } else {
      setEnterPressed(false);
    }
  };
  useEffect(() => {
    const selectElement = document.getElementById("searchSelect");

    if (selectElement) {
      selectElement.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      if (selectElement) {
        selectElement.removeEventListener("keydown", handleKeyPress);
      }
    };
  }, []);
  const sortedUsers = sortBy(allUsers.data, "name");
  return (
    <div className="chat-window newChat w-100">
      <div className="chat-header" style={{ paddingRight: "0px" }}>
        <div className="selectMemeber">
          <h4 className="m-0">Send To:</h4>
          <Select
            // Force component re-mount when loading state changes to clear Ant Design's internal option caching
            key={`select-${allUsers?.isLoading ? "loading" : "loaded"}`}
            showSearch
            placeholder="Choose a recipient..."
            optionFilterProp="children"
            value={searchTerm || ""}
            filterOption={false}
            onChange={handleMemberSelect}
            onSearch={handleDebouncedSearch}
            onDropdownVisibleChange={handleDropdownOpenChange}
            onFocus={handleFocus}
            open={dropdownOpen}
            ref={selectRef}
            className="selectMemberDropdown chat-member-select-dropdown"
            popupClassName="newChatDropdown"
            suffixIcon={false}
            id={"searchSelect"}
          >
            {allUsers?.isLoading ? (
              <Option key="loading" value="" disabled>
                <div className="d-flex justify-content-center align-items-center w-100">
                  <Image
                    width={30}
                    height={30}
                    src={Assets.loadingGif}
                    preview={false}
                  />
                  <span className="ms-2">Loading...</span>
                </div>
              </Option>
            ) : (
              sortedUsers
                .filter((member) => member?.id != authUser?.id)
                .map((member) => {
                  return (
                    <Option key={member?.id} value={member.id}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="title d-flex align-items-center">
                          <AvatarImage
                            name={member?.name}
                            src={member?.thumbnail ?? member?.image}
                            size={32}
                            placeholder={Assets.ProfilePlaceholder}
                          />
                          <span className="mb-0 ms-3">{member?.name}</span>
                        </div>
                        <span className="roundedIcon "></span>
                      </div>
                    </Option>
                  );
                })
            )}
          </Select>
        </div>
        <div>
          <button
            className="btn-gradient"
            style={{ borderRadius: "50px" }}
            onClick={() => {
              dispatch(
                setCreateUpdateGroupForm({
                  show: true,
                  formType: "create",
                }),
              );
            }}
          >
            <span>
              <AddIcon />
            </span>
            Create Group
          </button>
        </div>
      </div>

      <GenericModal
        resetForm={resetForm}
        show={createUpdateGroupForm.show}
        onHide={() => {
          dispatch(
            setCreateUpdateGroupForm({
              show: false,
              formType: null,
            }),
          );
        }}
        setModalShow={(e) => {
          dispatch(
            setCreateUpdateGroupForm({
              show: false,
              formType: null,
            }),
          );
        }}
        modalTitle={
          createUpdateGroupForm.formType == "update"
            ? "Edit Group"
            : "Create New Group"
        }
        pro={"Pro Tip:"}
        modalDesc={
          <>
            <div style={{ padding: "0 38px", textAlign: "left" }}>
              <p style={{ paddingTop: "20px", margin: 0, lineHeight: 1.5 }}>
                <b>Pro Tip:</b> When you create a group on desktop, these groups
                will appear under the "Recipients" dropdown when you assign a
                task, post an event, add a resource, and so on.&nbsp;
                <a
                  href={
                    "https://support.mysalonsymphony.com/hc/en-us/articles/18501449795611-User-Groups"
                  }
                  target="_blank"
                  className={"uLine globalBlueLinkColor"}
                  rel="noreferrer"
                >
                  Learn More
                </a>
                .
              </p>
            </div>
          </>
        }
        size={"lg"}
        bodyComponent={<CreateNewGroup />}
      />
    </div>
  );
});

NewChat.displayName = "NewChat";

export default NewChat;
